import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sprekersApi } from '../api';
import type { CanvasFrameFieldPatch } from './canvasEditorTypes';
import type { CanvasFrame, CanvasFrameShape } from '../types';

const NONE_PARENT = '__none__';

const SHAPE_OPTIONS: { value: CanvasFrameShape; label: string }[] = [
  { value: 'theme', label: 'Thema (dominant)' },
  { value: 'rounded', label: 'Afgeronde rechthoek' },
  { value: 'panel', label: 'Paneel met accentrand' },
  { value: 'large', label: 'Groot vlak' },
  { value: 'borderless', label: 'Kaderloos' },
  { value: 'circle', label: 'Cirkel' },
];

interface Props {
  frame: CanvasFrame;
  otherFrames: CanvasFrame[];
  onChange: (patch: CanvasFrameFieldPatch) => void;
  onDelete: () => void;
}

/**
 * Formulier (geen inline WYSIWYG) voor het geselecteerde frame -- betrouwbaar
 * bewerken van tekstvelden staat boven een subtielere maar foutgevoeligere
 * contentEditable-aanpak, "plain en simpel" past bij deze keuze.
 */
export default function CanvasFrameInspector({ frame, otherFrames, onChange, onDelete }: Props) {
  const [draft, setDraft] = useState(frame);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setDraft(frame); }, [frame.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitField = (field: keyof CanvasFrameFieldPatch) => {
    const value = draft[field as keyof CanvasFrame];
    if (value !== frame[field as keyof CanvasFrame]) onChange({ [field]: value } as CanvasFrameFieldPatch);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const media = await sprekersApi.uploadMedia(file);
      onChange({ media_id: media.id, media_type: file.type.startsWith('video') ? 'video' : 'afbeelding' });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Uploaden mislukt');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Frame bewerken</h3>
        <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Frame verwijderen">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-slate-400">Wijzigingen worden automatisch opgeslagen zodra je een veld verlaat.</p>

      <div>
        <Label htmlFor="frame-shape">Vorm</Label>
        <Select
          value={draft.shape}
          onValueChange={(value: CanvasFrameShape) => { setDraft({ ...draft, shape: value }); onChange({ shape: value }); }}
        >
          <SelectTrigger id="frame-shape"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SHAPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="frame-level">Niveau</Label>
        <Select
          value={String(draft.level)}
          onValueChange={(value: string) => { const level = Number(value); setDraft({ ...draft, level }); onChange({ level }); }}
        >
          <SelectTrigger id="frame-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4].map((level) => <SelectItem key={level} value={String(level)}>{level}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="frame-parent">Onderdeel van</Label>
        <Select
          value={draft.parent_frame_id || NONE_PARENT}
          onValueChange={(value: string) => {
            const parent = value === NONE_PARENT ? null : value;
            setDraft({ ...draft, parent_frame_id: parent });
            onChange({ parent_frame_id: parent });
          }}
        >
          <SelectTrigger id="frame-parent"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_PARENT}>Geen</SelectItem>
            {otherFrames.map((f) => <SelectItem key={f.id} value={f.id}>{f.title || f.id}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="frame-eyebrow">Eyebrow</Label>
        <Input
          id="frame-eyebrow" value={draft.eyebrow || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, eyebrow: e.target.value })}
          onBlur={() => commitField('eyebrow')}
        />
      </div>
      <div>
        <Label htmlFor="frame-title">Titel</Label>
        <Input
          id="frame-title" value={draft.title || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, title: e.target.value })}
          onBlur={() => commitField('title')}
        />
      </div>
      <div>
        <Label htmlFor="frame-subtitle">Subtitel</Label>
        <Input
          id="frame-subtitle" value={draft.subtitle || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, subtitle: e.target.value })}
          onBlur={() => commitField('subtitle')}
        />
      </div>
      <div>
        <Label htmlFor="frame-ref">Verwijzing (bv. Bijbeltekst)</Label>
        <Input
          id="frame-ref" value={draft.ref || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, ref: e.target.value })}
          onBlur={() => commitField('ref')}
        />
      </div>
      <div>
        <Label htmlFor="frame-body">Tekst</Label>
        <Textarea
          id="frame-body" rows={4} value={draft.body || ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft({ ...draft, body: e.target.value })}
          onBlur={() => commitField('body')}
        />
      </div>
      <div>
        <Label htmlFor="frame-rotation">Kanteling (graden)</Label>
        <Input
          id="frame-rotation" type="number" step="0.1" value={draft.rotation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, rotation: Number(e.target.value) })}
          onBlur={() => commitField('rotation')}
        />
      </div>

      <div>
        <Label htmlFor="frame-media">Media (afbeelding/video, optioneel)</Label>
        <Input id="frame-media" type="file" accept="image/*,video/*" onChange={handleFileChange} disabled={uploading} />
        {uploading && <p className="mt-1 text-xs text-slate-400">Uploaden...</p>}
        {frame.media_id && (
          <Button
            type="button" variant="link" size="sm" className="mt-1 h-auto p-0 text-xs"
            onClick={() => onChange({ media_id: null, media_type: null })}
          >
            Media verwijderen
          </Button>
        )}
      </div>
    </div>
  );
}
